// Push Notification Service with Firebase Cloud Messaging
import { getFCMToken, onForegroundMessage } from '../config/firebase';
import ApiService from './ApiService';

// VAPID Key from Firebase Console -> Project Settings -> Cloud Messaging -> Web Push certificates
const VAPID_KEY = 'BOIhzCdoafAI5zobFGJUVRElntfOshiLjYGsMBygvWOAlpjEMaXpTYTC-h_Hjx12OFr9Ti8R_S065XI5WKp-Tcg';

const PushNotificationService = {
  fcmToken: null,
  unsubscribeForeground: null,

  // Перевірка підтримки сповіщень
  isSupported: () => {
    return "Notification" in window && "serviceWorker" in navigator;
  },

  // Перевірка чи вкладка прихована (не в фокусі)
  isPageHidden: () => {
    return document.hidden || document.visibilityState === "hidden";
  },

  // Перевірка чи вікно не в фокусі
  isWindowBlurred: () => {
    return !document.hasFocus();
  },

  // Чи потрібно показувати сповіщення (вкладка прихована АБО вікно не в фокусі)
  shouldShowNotification: () => {
    return PushNotificationService.isPageHidden() || PushNotificationService.isWindowBlurred();
  },

  // Отримати поточний статус дозволу
  getPermissionStatus: () => {
    if (!PushNotificationService.isSupported()) {
      return "not-supported";
    }
    return Notification.permission;
  },

  // Запит дозволу на сповіщення
  requestPermission: async () => {
    if (!PushNotificationService.isSupported()) {
      console.warn("Push notifications are not supported in this browser");
      return false;
    }

    if (Notification.permission === "granted") {
      return true;
    }

    if (Notification.permission === "denied") {
      console.warn("Push notifications were denied by user");
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      return permission === "granted";
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      return false;
    }
  },

  // Отримати FCM токен
  getToken: async () => {
    if (!VAPID_KEY) {
      console.warn("VAPID key not configured. Set REACT_APP_FIREBASE_VAPID_KEY in .env");
      return null;
    }

    try {
      const token = await getFCMToken(VAPID_KEY);
      if (token) {
        PushNotificationService.fcmToken = token;
        return token;
      }
      return null;
    } catch (error) {

      return null;
    }
  },

  // Зберегти FCM токен на сервері
  saveTokenToServer: async (token) => {
    try {
      await ApiService.post('device-tokens', { token: token, platform: "web" });

      return true;
    } catch (error) {

      return false;
    }
  },

  // Видалити FCM токен з сервера
  removeTokenFromServer: async (token) => {
    try {
      await ApiService.delete('device-tokens', { token: token });

      return true;
    } catch (error) {

      return false;
    }
  },

  // Показати сповіщення через Service Worker (надійніше)
  show: async (title, options = {}) => {
    console.log("PushNotificationService.show called:", title, options);
    console.log("Notification.permission:", Notification.permission);

    if (!PushNotificationService.isSupported()) {
      console.warn("Push notifications are not supported");
      return null;
    }

    if (Notification.permission !== "granted") {
      console.warn("Push notification permission not granted");
      return null;
    }

    const defaultOptions = {
      icon: "/logo192.png",
      badge: "/favicon-96x96.png",
      vibrate: [200, 100, 200],
      tag: options.tag || `nachmed-${Date.now()}`,
      renotify: true,
      requireInteraction: false,
      ...options,
    };

    try {
      // Спробувати через Service Worker
      const registration = await navigator.serviceWorker.ready;
      console.log("Using Service Worker for notification");
      await registration.showNotification(title, defaultOptions);
      console.log("Service Worker notification shown");
      return true;
    } catch (swError) {
      console.warn("Service Worker notification failed, trying direct:", swError);

      // Fallback на прямий Notification API
      try {
        const notification = new Notification(title, defaultOptions);
        console.log("Direct notification created:", notification);

        notification.onclick = (event) => {
          event.preventDefault();
          window.focus();
          notification.close();
          if (options.onClick) {
            options.onClick(event);
          }
        };

        return notification;
      } catch (error) {
        console.error("Error showing notification:", error);
        return null;
      }
    }
  },

  // Слухати foreground повідомлення
  setupForegroundListener: (onMessageCallback) => {
    PushNotificationService.unsubscribeForeground = onForegroundMessage((payload) => {
      console.log('FCM Foreground payload:', payload);

      const title = payload.notification?.title || payload.data?.title || 'Nachmed AI';
      const body = payload.notification?.body || payload.data?.body || 'Нове повідомлення';

      // Завжди показуємо сповіщення з унікальним tag
      PushNotificationService.show(title, {
        body: body,
        tag: `nachmed-${Date.now()}`,
      });

      // Виклик колбеку для оновлення UI
      if (onMessageCallback) {
        onMessageCallback(payload);
      }
    });
  },

  // Ініціалізація FCM
  init: async (onMessageCallback) => {
    const granted = await PushNotificationService.requestPermission();

    if (!granted) {
      console.warn("Push notifications permission not granted");
      return false;
    }

    console.log("Push notifications enabled");

    // Отримати FCM токен
    const token = await PushNotificationService.getToken();

    if (token) {
      // Зберегти токен на сервері
      await PushNotificationService.saveTokenToServer(token);

      // Налаштувати foreground listener
      PushNotificationService.setupForegroundListener(onMessageCallback);

      return true;
    }

    return false;
  },

  // Очистити підписки
  cleanup: async () => {
    if (PushNotificationService.fcmToken) {
      await PushNotificationService.removeTokenFromServer(PushNotificationService.fcmToken);
      PushNotificationService.fcmToken = null;
    }
    if (PushNotificationService.unsubscribeForeground) {
      PushNotificationService.unsubscribeForeground();
      PushNotificationService.unsubscribeForeground = null;
    }
  },
};

export default PushNotificationService;
